import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Loader2, Users, Trash2, ChevronRight, QrCode, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Attendance() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance/classes");
      setClasses(data);
      if (!selected && data.length) setSelected(data[0]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, []);

  return (
    <Layout title="Smart Attendance">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <aside className="lg:col-span-1 space-y-3" data-testid="classes-sidebar">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base font-medium">Classes</h3>
            <NewClassDialog onCreated={refresh} />
          </div>

          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loading && classes.length === 0 && (
            <div className="tb-card p-5 text-sm text-muted-foreground">
              No classes yet. Create your first class to get started.
            </div>
          )}
          <div className="space-y-2">
            {classes.map((c) => (
              <button
                key={c.class_id}
                onClick={() => setSelected(c)}
                data-testid={`class-item-${c.class_id}`}
                className={`w-full text-left rounded-2xl border p-4 transition ${
                  selected?.class_id === c.class_id
                    ? "border-violet-300 bg-violet-50/60 dark:bg-violet-500/10 dark:border-violet-500/30"
                    : "border-border bg-card hover:bg-muted"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-display text-sm font-medium truncate">{c.name}</p>
                    <p className="text-[11px] text-muted-foreground">{c.subject} · Grade {c.grade}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>
        </aside>

        <section className="lg:col-span-3">
          {selected ? (
            <ClassDetail
              classroom={selected}
              onDeleted={() => {
                setSelected(null);
                refresh();
              }}
            />
          ) : (
            <div className="tb-card p-12 text-center text-muted-foreground">
              <Users className="h-8 w-8 mx-auto opacity-50" />
              <p className="mt-3 font-display">Select or create a class to begin</p>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

function NewClassDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", subject: "", grade: "", schedule: "" });

  const create = async () => {
    if (!form.name || !form.subject || !form.grade) {
      toast.error("Please fill name, subject and grade");
      return;
    }
    setBusy(true);
    try {
      await api.post("/attendance/classes", form);
      setOpen(false);
      setForm({ name: "", subject: "", grade: "", schedule: "" });
      onCreated();
      toast.success("Class created");
    } catch {
      toast.error("Failed to create class");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full" data-testid="new-class-button">
          <Plus className="h-4 w-4 mr-1" /> New
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New class</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Class 9-A Biology"
              data-testid="new-class-name"
              className="mt-1.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Subject</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Biology"
                data-testid="new-class-subject"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Grade</Label>
              <Input
                value={form.grade}
                onChange={(e) => setForm({ ...form, grade: e.target.value })}
                placeholder="9"
                data-testid="new-class-grade"
                className="mt-1.5"
              />
            </div>
          </div>
          <div>
            <Label>Schedule (optional)</Label>
            <Input
              value={form.schedule}
              onChange={(e) => setForm({ ...form, schedule: e.target.value })}
              placeholder="Mon/Wed/Fri 10:00"
              className="mt-1.5"
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={busy} data-testid="create-class-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClassDetail({ classroom, onDeleted }) {
  const [students, setStudents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSession, setOpenSession] = useState(null);
  const [marks, setMarks] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get(`/attendance/classes/${classroom.class_id}/students`),
        api.get(`/attendance/classes/${classroom.class_id}/analytics`),
      ]);
      setStudents(s.data);
      setAnalytics(a.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setOpenSession(null);
    setMarks([]);
    // eslint-disable-next-line
  }, [classroom.class_id]);

  const addStudent = async (data) => {
    await api.post(`/attendance/classes/${classroom.class_id}/students`, data);
    load();
  };

  const removeStudent = async (id) => {
    await api.delete(`/attendance/students/${id}`);
    load();
  };

  const startSession = async () => {
    const { data } = await api.post(`/attendance/classes/${classroom.class_id}/sessions`);
    setOpenSession(data);
    setMarks([]);
    toast.success("Attendance session opened");
  };

  const closeSession = async () => {
    if (!openSession) return;
    const { data } = await api.post(`/attendance/sessions/${openSession.session_id}/close`);
    setOpenSession(data);
    toast.success("Session closed");
    load();
  };

  const mark = async (student_id, status) => {
    if (!openSession) return;
    const { data } = await api.post(`/attendance/sessions/${openSession.session_id}/mark`, {
      student_id,
      status,
    });
    setMarks((m) => {
      const filtered = m.filter((x) => x.student_id !== student_id);
      return [...filtered, data];
    });
  };

  const deleteClass = async () => {
    if (!window.confirm("Delete this class and all data?")) return;
    await api.delete(`/attendance/classes/${classroom.class_id}`);
    onDeleted();
  };

  return (
    <div className="space-y-5">
      <div className="tb-card p-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-2xl tracking-tight font-medium">{classroom.name}</h2>
          <p className="text-sm text-muted-foreground">
            {classroom.subject} · Grade {classroom.grade}
            {classroom.schedule ? ` · ${classroom.schedule}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!openSession && (
            <Button onClick={startSession} className="rounded-full" data-testid="start-session-button">
              <QrCode className="h-4 w-4 mr-1.5" /> Start attendance
            </Button>
          )}
          {openSession && openSession.is_open && (
            <Button onClick={closeSession} variant="outline" className="rounded-full" data-testid="close-session-button">
              Close session
            </Button>
          )}
          <Button onClick={deleteClass} variant="ghost" size="icon" className="text-destructive">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="class-stats">
        <Stat label="Students" value={students.length} chip="tb-chip-mint" />
        <Stat label="Sessions" value={analytics?.sessions_count || 0} chip="tb-chip-butter" />
        <Stat label="Avg attendance" value={`${analytics?.avg_attendance_pct || 0}%`} chip="tb-chip-lavender" />
        <Stat label="Live session" value={openSession?.is_open ? "Open" : "—"} chip="tb-chip-mint" />
      </div>

      {openSession && (
        <div className="tb-card p-6" data-testid="active-session">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Live session · {openSession.date}</p>
              <p className="font-display text-base font-medium mt-1">Mark attendance</p>
            </div>
            <span className={`text-[11px] rounded-full px-2 py-0.5 ${openSession.is_open ? "tb-chip-mint" : "tb-chip-butter"}`}>
              {openSession.is_open ? "OPEN" : "CLOSED"}
            </span>
          </div>
          {students.length === 0 ? (
            <p className="text-sm text-muted-foreground">Add students first to mark attendance.</p>
          ) : (
            <div className="space-y-2">
              {students.map((s) => {
                const m = marks.find((x) => x.student_id === s.student_id);
                return (
                  <div
                    key={s.student_id}
                    className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-3"
                    data-testid={`mark-row-${s.student_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{s.name}</p>
                      <p className="text-[11px] text-muted-foreground">Roll: {s.roll_no || "—"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <MarkBtn active={m?.status === "present"} onClick={() => mark(s.student_id, "present")} icon={<CheckCircle2 className="h-3.5 w-3.5" />} label="Present" tone="emerald" testid={`present-${s.student_id}`} />
                      <MarkBtn active={m?.status === "late"} onClick={() => mark(s.student_id, "late")} icon={<Clock className="h-3.5 w-3.5" />} label="Late" tone="amber" testid={`late-${s.student_id}`} />
                      <MarkBtn active={m?.status === "absent"} onClick={() => mark(s.student_id, "absent")} icon={<XCircle className="h-3.5 w-3.5" />} label="Absent" tone="rose" testid={`absent-${s.student_id}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="tb-card p-6" data-testid="students-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-base font-medium">Roster</h3>
          <AddStudentForm onAdd={addStudent} />
        </div>
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && students.length === 0 && (
          <p className="text-sm text-muted-foreground">No students yet.</p>
        )}
        <div className="space-y-2">
          {students.map((s) => (
            <div key={s.student_id} className="flex items-center gap-3 rounded-xl border border-border p-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{s.name}</p>
                <p className="text-[11px] text-muted-foreground">Roll: {s.roll_no || "—"} {s.email ? `· ${s.email}` : ""}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => removeStudent(s.student_id)} className="text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {analytics?.students?.length > 0 && (
        <div className="tb-card p-6" data-testid="analytics-card">
          <h3 className="font-display text-base font-medium">Per-student analytics</h3>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Present</th>
                  <th className="py-2 pr-4">Late</th>
                  <th className="py-2 pr-4">Absent</th>
                  <th className="py-2 pr-4">Rate</th>
                </tr>
              </thead>
              <tbody>
                {analytics.students.map((row) => (
                  <tr key={row.student_id} className="border-t border-border">
                    <td className="py-2 pr-4">{row.name}</td>
                    <td className="py-2 pr-4">{row.present}</td>
                    <td className="py-2 pr-4">{row.late}</td>
                    <td className="py-2 pr-4">{row.absent}</td>
                    <td className="py-2 pr-4 font-medium">{row.rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function MarkBtn({ active, icon, label, onClick, tone, testid }) {
  const cls = {
    emerald: active ? "bg-emerald-500 text-white" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-200",
    amber: active ? "bg-amber-500 text-white" : "bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-500/15 dark:text-amber-200",
    rose: active ? "bg-rose-500 text-white" : "bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-500/15 dark:text-rose-200",
  }[tone];
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${cls}`}
    >
      {icon} {label}
    </button>
  );
}

function AddStudentForm({ onAdd }) {
  const [name, setName] = useState("");
  const [roll, setRoll] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await onAdd({ name: name.trim(), roll_no: roll.trim() || null });
      setName("");
      setRoll("");
    } catch {
      toast.error("Failed to add");
    }
  };

  return (
    <form onSubmit={submit} className="flex gap-2 items-center">
      <Input
        placeholder="Student name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="h-9 rounded-full w-44"
        data-testid="student-name-input"
      />
      <Input
        placeholder="Roll"
        value={roll}
        onChange={(e) => setRoll(e.target.value)}
        className="h-9 rounded-full w-20"
        data-testid="student-roll-input"
      />
      <Button type="submit" size="sm" className="rounded-full" data-testid="add-student-button">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add
      </Button>
    </form>
  );
}

function Stat({ label, value, chip }) {
  return (
    <div className="tb-card p-4">
      <p className={`inline-flex whitespace-nowrap text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2 py-0.5 ${chip}`}>
        {label}
      </p>
      <p className="mt-2 font-display text-xl tracking-tight font-medium">{value}</p>
    </div>
  );
}
